import { useState } from 'react';
import { ArrowSquareOut, EnvelopeSimple, FileText, Phone, SignOut, User, UserPlus } from '@phosphor-icons/react';
import { ImmoButton, InnenKarte, KartenTitel, LeerZustand, ToastPill, useToastPill } from '../../../../../components/immobilien/ui';
import { deDatum } from '../../../../../lib/immobilien/objektDetail';
import { initialen } from '../../../../../lib/immobilien/format';
import { mietdauer } from '../../../../../lib/immobilien/einheitDetail';
import { useOpenKontakt } from '../../../../../lib/immobilien/openKontakt';
import { usePortalStatus } from '../../../../../lib/immobilien/usePortalStatus';
import type { MietverhaeltnisRoh } from '../../../../../lib/immobilien/useEinheitDetail';
import { kontaktName } from '../../../../../lib/objekte/objektKontakte';
import { supabase } from '../../../../../lib/supabase/client';
import { useEinheitSeite } from '../EinheitSeitenKontext';
import { PortalKarte } from './PortalKarte';
import { DatenZeile, KartenRaster } from './teile';



/** Bereich „Mieter“ (Screens 35/36): Kopfkarte, Mieterportal, Kontakt, Haushalt. */
export function MieterBereich() {
  const s = useEinheitSeite();
  const openKontakt = useOpenKontakt();
  if (!s.aktuell) {
    return (
      <LeerZustand icon={User} titel="Leerstand" text="Diese Einheit hat kein laufendes Mietverhältnis.">
        <ImmoButton art="akzent" icon={UserPlus} onClick={() => s.oeffneBereich('mv')}>Mieter zuordnen</ImmoButton>
      </LeerZustand>
    );
  }
  return (
    <>
      {s.laufende.map((m, i) => <Mietpartei key={m.id} m={m} weitere={i > 0} openKontakt={openKontakt} />)}
    </>
  );
}

function Mietpartei({ m, weitere, openKontakt }: { m: MietverhaeltnisRoh; weitere: boolean; openKontakt: ReturnType<typeof useOpenKontakt> }) {
  const s = useEinheitSeite();
  const portal = usePortalStatus(m.email);
  const [toast, zeige] = useToastPill();
  const [oeffnet, setOeffnet] = useState(false);
  const name = kontaktName(m);
  const telefon = (m.mobil ?? '').trim() || (m.telefon ?? '').trim();
  const email = (m.email ?? '').trim();
  const mitmieter = s.daten.mitmieter.filter(x => x.hauptId === m.id);
  const personen = m.anzahl_bewohner ?? 1 + mitmieter.length;
  const dauer = mietdauer(m.mietbeginn, s.heute);


  // Wie bisher: neuester Mietvertrag des Mieters aus dem Speicher „dokumente“ (signierte URL, 1 Stunde)
  const mietvertrag = async () => {
    setOeffnet(true);
    const fenster = window.open('', '_blank');
    if (fenster) fenster.opener = null;
    try {
      const { data, error } = await supabase.from('dokumente').select('datei_url').eq('mieter_id', m.id).eq('is_deleted', false)
        .eq('kategorie', 'Mietvertrag').order('created_at', { ascending: false }).limit(1);
      if (error) throw new Error(error.message);
      const pfad = (data as { datei_url: string | null }[] | null)?.[0]?.datei_url;
      if (!pfad) { fenster?.close(); zeige('Kein Mietvertrag hinterlegt – im Mietverhältnis hochladen', true); return; }
      const signiert = await supabase.storage.from('dokumente').createSignedUrl(pfad, 3600);
      if (!signiert.data?.signedUrl) throw new Error('Mietvertrag konnte nicht geöffnet werden');
      if (fenster) fenster.location.href = signiert.data.signedUrl; else window.location.assign(signiert.data.signedUrl);
    } catch (e) {
      fenster?.close();
      zeige(e instanceof Error ? e.message : 'Mietvertrag konnte nicht geöffnet werden', true);
    } finally {
      setOeffnet(false);
    }
  };

  const rund = { width: 46, height: 46, borderRadius: 15, border: 'none', background: 'rgba(255,255,255,0.14)', color: 'var(--acc-ink)' } as const;

  return (
    <>
      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
      {weitere && <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--ink3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: 8 }}>Weitere Mietpartei</span>}

      <div className="immo-akzent flex items-center flex-wrap" style={{ borderRadius: 26, padding: 20, gap: 16 }}>
        <span className="flex items-center justify-center shrink-0" style={{ width: 70, height: 70, borderRadius: '50%', background: 'rgba(255,255,255,0.14)', fontSize: 22, fontWeight: 600 }}>{initialen(name)}</span>
        <div className="flex flex-col flex-1 min-w-0" style={{ gap: 4 }}>
          <button type="button" onClick={() => openKontakt(m.id, 'mieter')} className="truncate text-left cursor-pointer"
            style={{ border: 'none', background: 'transparent', padding: 0, fontSize: 26, fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--acc-ink)' }}>{name}</button>
          <span className="truncate" style={{ fontSize: 13, color: 'var(--acc-ink2)' }}>
            {[m.mietbeginn ? `Mieter seit ${deDatum(m.mietbeginn)}` : 'Mieter', dauer, personen ? `${personen} ${personen === 1 ? 'Person' : 'Personen'}` : ''].filter(Boolean).join(' · ')}
          </span>
        </div>
        <div className="flex" style={{ gap: 8 }}>
          {telefon ? <a href={`tel:${telefon.replace(/[^\d+]/g, '')}`} aria-label={`${name} anrufen`} className="flex items-center justify-center" style={rund}><Phone weight="fill" size={18} /></a>
            : <span title="Keine Telefonnummer hinterlegt" className="flex items-center justify-center" style={{ ...rund, opacity: 0.4 }}><Phone weight="fill" size={18} /></span>}
          {email ? <a href={`mailto:${email}`} aria-label={`E-Mail an ${name}`} className="flex items-center justify-center" style={rund}><EnvelopeSimple weight="fill" size={18} /></a>
            : <span title="Keine E-Mail hinterlegt" className="flex items-center justify-center" style={{ ...rund, opacity: 0.4 }}><EnvelopeSimple weight="fill" size={18} /></span>}
          <button type="button" aria-label="Kontakt öffnen" title="Kontakt öffnen" onClick={() => openKontakt(m.id, 'mieter')} className="flex items-center justify-center cursor-pointer" style={rund}>
            <ArrowSquareOut weight="bold" size={18} />
          </button>
        </div>
      </div>

      <PortalKarte mieterName={name} mieterId={m.id} email={email} status={portal.status} onGeaendert={portal.neuLaden}
        onKontakt={() => openKontakt(m.id, 'mieter')} zeige={zeige} />

      <KartenRaster>
        <InnenKarte>
          <KartenTitel>Kontakt</KartenTitel>
          <div className="flex flex-col">
            <DatenZeile label="Telefon" wert={(m.telefon ?? '').trim()} zahl />
            <DatenZeile label="Mobil" wert={(m.mobil ?? '').trim()} zahl />
            <DatenZeile label="E-Mail" wert={email} />
            <DatenZeile label="Mietbeginn" wert={m.mietbeginn ? deDatum(m.mietbeginn) : ''} zahl />
            {m.mietende && <DatenZeile label="Mietende" wert={deDatum(m.mietende)} zahl />}
            {m.vertragsart && <DatenZeile label="Vertragsart" wert={m.vertragsart} />}
          </div>
          <ImmoButton icon={FileText} disabled={oeffnet} onClick={() => { void mietvertrag(); }} style={{ alignSelf: 'flex-start' }}>Mietvertrag einsehen</ImmoButton>
        </InnenKarte>
        <InnenKarte>
          <KartenTitel>Haushalt</KartenTitel>
          <div className="flex flex-col" style={{ gap: 10 }}>
            {[{ id: m.id, name, rolle: 'Hauptmieter' }, ...mitmieter.map(x => ({ id: x.id, name: kontaktName(x), rolle: 'Mitbewohner' }))].map(p => (
              <button key={p.id} type="button" onClick={() => openKontakt(p.id, 'mieter')} className="immo-hover-flaeche flex items-center text-left cursor-pointer"
                style={{ gap: 12, padding: 6, margin: -6, borderRadius: 14, border: 'none', background: 'transparent', color: 'var(--ink)' }}>
                <span className="flex items-center justify-center shrink-0" style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--fill2)', fontSize: 13, fontWeight: 700 }}>{initialen(p.name)}</span>
                <span className="flex flex-col min-w-0">
                  <span className="truncate" style={{ fontSize: 14, fontWeight: 700 }}>{p.name}</span>
                  <span style={{ fontSize: 12, color: 'var(--ink3)' }}>{p.rolle}</span>
                </span>
              </button>
            ))}
            {m.anzahl_bewohner != null && m.anzahl_bewohner > 1 + mitmieter.length && (
              <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Insgesamt {m.anzahl_bewohner} Bewohner laut Mietverhältnis</span>
            )}
          </div>
          <ImmoButton icon={SignOut} onClick={() => s.bearbeiteMietverhaeltnis(m, 'vertrag')} style={{ alignSelf: 'flex-start' }}>Kündigung erfassen</ImmoButton>
        </InnenKarte>
      </KartenRaster>
    </>
  );
}
